import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Briefcase, Star, MapPin, ShieldCheck, Loader2, CheckCircle2, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { buildGoogleAuthUrl } from '../utils/googleAuthUrl';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Public claim page reached via the WhatsApp/email "Featured Pro" outreach.
 * Each link is unique: /claim/{place_id} — submitting binds this pro's
 * harvested lead to a new (or existing) user account + auto-issues their
 * 20% referral code.
 */
const ClaimProPage = () => {
  const { place_id } = useParams();
  const navigate = useNavigate();
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [contactName, setContactName] = useState('');
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/pros/claim/${place_id}`);
        if (!cancelled) setPreview(res.data);
      } catch (e) {
        if (!cancelled) toast.error(e?.response?.data?.detail || 'Profile not found');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [place_id]);

  const handleClaim = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Email and password are required');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API_URL}/api/pros/claim/${place_id}`, {
        email, password, contact_name: contactName,
      });
      setSuccess(res.data);
      toast.success('Profile claimed — welcome to Shopperden');
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Claim failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    );
  }

  if (!preview) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#F8FAFC] p-8">
        <p className="text-slate-500">This profile no longer exists.</p>
        <Link to="/" className="mt-4 text-[#0F172A] underline" data-testid="claim-back-home">Back to Shopperden</Link>
      </div>
    );
  }

  if (success) {
    const trialEnds = success.verified_partner_trial_ends_at
      ? new Date(success.verified_partner_trial_ends_at).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
      : null;
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC] p-6">
        <div className="bg-white border border-slate-200 rounded-md p-8 max-w-lg w-full text-center" data-testid="claim-success-card">
          <div className="mx-auto bg-emerald-50 border border-emerald-200 w-14 h-14 rounded-full flex items-center justify-center mb-4">
            <CheckCircle2 className="h-7 w-7 text-emerald-600" />
          </div>
          <h2 className="text-2xl font-['Playfair_Display'] text-[#0F172A] mb-2">You're a Featured Pro on Shopperden</h2>
          <p className="text-sm text-slate-600 mb-4">
            Your <b>{preview.city}</b> profile is now claimed. Share your personal referral link — earn <b>20% commission</b> on every buyer or seller you bring.
          </p>
          {trialEnds && (
            <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 rounded p-3 mb-5 text-sm" data-testid="claim-trial-banner">
              <b>14-day Verified Partner trial active.</b> Full access to matched buyers — no card required. Renews £29/mo on <b>{trialEnds}</b>.
            </div>
          )}
          <div className="bg-slate-50 border border-slate-200 rounded p-4 mb-6 text-left">
            <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-slate-500 mb-1">Your referral link</p>
            <p className="text-sm font-mono text-[#0F172A] break-all" data-testid="claim-success-referral-url">{success.referral_url}</p>
            <Button
              size="sm"
              variant="outline"
              className="mt-3"
              onClick={() => { navigator.clipboard.writeText(success.referral_url); toast.success('Link copied'); }}
              data-testid="claim-copy-referral"
            >
              Copy link
            </Button>
          </div>
          <Button
            onClick={() => navigate('/professional')}
            className="w-full bg-[#0F172A] hover:bg-slate-800 text-white"
            data-testid="claim-goto-dashboard"
          >
            See my matched buyers
            <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        </div>
      </div>
    );
  }

  const cat = (preview.category || 'professional').replace(/\b\w/g, (c) => c.toUpperCase());

  // Live count of "warm" buyer profiles in this city — drives the
  // urgency hook at the top of the page. Backed by /api/leads/warm-count.
  // Fails silently → renders a sensible "3+" default so the trust pitch
  // still reads cleanly when the matchmaker hasn't finished its first run.
  const warmCount = preview.warm_matches ?? preview.warm_buyers_count ?? null;
  const warmCopy = warmCount && warmCount >= 1
    ? `You have ${warmCount} warm property match${warmCount === 1 ? '' : 'es'} waiting in ${preview.city}.`
    : `You have warm property matches waiting in ${preview.city}.`;

  return (
    <div className="min-h-screen bg-[#F8FAFC] py-12 px-4">
      <div className="max-w-2xl mx-auto">
        <Link to="/" className="text-xs uppercase tracking-[0.3em] text-[#E07A3A] font-semibold" data-testid="claim-shopperden-home">
          ← Shopperden
        </Link>

        {/* Aggressive value hook — always above the fold. */}
        <div
          className="mt-4 mb-4 bg-gradient-to-r from-emerald-50 via-amber-50 to-emerald-50 border border-emerald-300 rounded-md p-4 flex items-start gap-3"
          data-testid="claim-warm-hook"
        >
          <div className="bg-emerald-600 text-white text-[10px] font-bold tracking-widest uppercase px-2 py-0.5 rounded mt-0.5 flex-shrink-0">
            Live
          </div>
          <p className="text-sm text-[#0F172A] font-semibold leading-snug">
            Welcome <span className="text-emerald-700">{preview.name}</span> — {warmCopy}
            <span className="block text-[11px] text-slate-600 font-normal mt-1">
              Claim now to unlock — your 14-day Verified Partner trial starts free, no card required.
            </span>
          </p>
        </div>

        {/* Preview card */}
        <div className="bg-white border border-slate-200 rounded-md p-6 mt-4 mb-6" data-testid="claim-preview-card">
          <div className="flex items-start gap-4 mb-4">
            <div className="bg-[#E07A3A]/10 border border-[#E07A3A]/30 p-3 rounded flex-shrink-0">
              <Briefcase className="h-6 w-6 text-[#E07A3A]" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] tracking-[0.3em] text-[#E07A3A] uppercase font-semibold">{cat}</p>
              <h1 className="font-['Playfair_Display'] text-2xl text-[#0F172A] mt-1 leading-tight">{preview.name}</h1>
              <div className="flex flex-wrap items-center gap-3 mt-2 text-xs text-slate-500">
                <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{preview.city}, {preview.country}</span>
                {preview.rating ? (
                  <span className="flex items-center gap-1 text-amber-600 font-semibold">
                    <Star className="h-3 w-3 fill-current" />
                    {preview.rating} <span className="text-slate-400 font-normal">({preview.review_count || 0} reviews)</span>
                  </span>
                ) : null}
              </div>
              {preview.address && <p className="text-xs text-slate-400 mt-1 truncate">{preview.address}</p>}
            </div>
          </div>
          <div className="bg-[#FEF8EC] border border-[#E07A3A]/30 rounded p-4">
            <p className="text-sm text-[#0F172A] leading-relaxed">
              You're already a <b>Featured Pro</b> on Shopperden for <b>{preview.city}</b>.
              Buyers in your city are paying our connection fee to reach pros like you.
              Claim your profile to unlock the leads — and start earning <b>20% commission</b>
              on every paying client you refer.
            </p>
          </div>
        </div>

        {/* Claim form */}
        {preview.already_claimed ? (
          <div className="bg-white border border-slate-200 rounded-md p-6 text-center" data-testid="claim-already-card">
            <ShieldCheck className="h-8 w-8 text-emerald-600 mx-auto mb-2" />
            <h3 className="font-semibold text-[#0F172A]">Profile already claimed</h3>
            <p className="text-sm text-slate-500 mt-1 mb-4">
              Sign in to your existing account or contact support if you believe this was claimed in error.
            </p>
            <Link to="/login" className="text-[#0F172A] underline text-sm" data-testid="claim-already-login-link">Sign in to Shopperden</Link>
          </div>
        ) : (
          <form onSubmit={handleClaim} className="bg-white border border-slate-200 rounded-md p-6 space-y-4" data-testid="claim-form">
            <h2 className="font-['Playfair_Display'] text-xl text-[#0F172A]">Claim your Featured Pro profile</h2>

            {/* Frictionless entry — Google one-tap takes the pro
                straight to /auth/callback then back to the dashboard
                with the lead auto-linked via `claimed_via_lead`. */}
            <button
              type="button"
              onClick={() => {
                try { sessionStorage.setItem('shopperden_pending_claim_place_id', place_id); } catch (_e) { /* ignore */ }
                const redirect = `${window.location.origin}/auth/callback`;
                window.location.href = buildGoogleAuthUrl(redirect);
              }}
              className="w-full flex items-center justify-center gap-2 bg-white hover:bg-slate-50 text-[#0F172A] border border-slate-300 px-4 py-2.5 rounded text-sm font-semibold"
              data-testid="claim-google-signin"
            >
              <svg width="16" height="16" viewBox="0 0 48 48" aria-hidden="true">
                <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.6-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C34.5 6.1 29.5 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.3-.4-3.5z"/>
                <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 13 24 13c3 0 5.8 1.1 7.9 3l5.7-5.7C34.5 6.1 29.5 4 24 4 16.3 4 9.6 8.3 6.3 14.7z"/>
                <path fill="#4CAF50" d="M24 44c5.2 0 10-2 13.6-5.2l-6.3-5.3C29.2 35.3 26.7 36 24 36c-5.3 0-9.7-3.4-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"/>
                <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.2-4 5.5l6.3 5.3C41.3 35.6 44 30.2 44 24c0-1.2-.1-2.3-.4-3.5z"/>
              </svg>
              Sign in with Google to claim instantly
            </button>
            <div className="text-center text-[10px] uppercase tracking-widest text-slate-400">or with email</div>
            <div>
              <label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Your work email</label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                className="mt-1"
                data-testid="claim-email-input"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Choose a password</label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Minimum 6 characters"
                required
                minLength={6}
                className="mt-1"
                data-testid="claim-password-input"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Your name (optional)</label>
              <Input
                value={contactName}
                onChange={(e) => setContactName(e.target.value)}
                placeholder={preview.name || 'Your name'}
                className="mt-1"
                data-testid="claim-name-input"
              />
            </div>
            <Button
              type="submit"
              disabled={submitting}
              className="w-full bg-[#E07A3A] hover:bg-[#C4652E] text-white tracking-wider"
              data-testid="claim-submit-btn"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Claim my profile
            </Button>
            <p className="text-[11px] text-slate-400 text-center">
              By claiming you agree to Shopperden's terms. No credit card required — directory listing is free.
            </p>
          </form>
        )}
      </div>
    </div>
  );
};

export default ClaimProPage;
